import * as React from 'react';
import {ErrorBoundaryFallbackUI} from '@stripe-internal/connect-embedded-lib';
import {view} from '@sail/ui';
import type {IntlHook} from '@sail/intl';
import {message, useIntl} from '@sail/intl';
import {isDevEnvironment} from '../utils/isDevEnvironment';
import {
  useReportConnectRenderError,
  type ReportConnectRenderError,
} from './utils/useReportConnectRenderError';

export type ConnectElementErrorBoundaryProps = {
  fallbackUI?: React.ReactNode;
  hasAuthError: boolean;
  onError?: (arg1: Error, arg2: React.ErrorInfo) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  uuid: string;
};

type ConnectElementBaseErrorBoundaryProps = ConnectElementErrorBoundaryProps & {
  intl: IntlHook;
  reportConnectError: ReportConnectRenderError;
  hasReportedErrorRef: React.MutableRefObject<boolean>;
};

type State = {
  error?: Error;
};

export const defaultErrorMessages = {
  defaultErrorMessageHeader: message({
    id: 'connectEmbed.errorBoundary.defaultMessage.header',
    defaultMessage: 'Something went wrong.',
    description: 'Message shown when an unhandled error is caught',
  }),

  defaultErrorMessageText: message({
    id: 'connectEmbed.errorBoundary.defaultMessage.text',

    defaultMessage:
      'Your money is secure, but your information cannot be loaded here.',

    description: 'Message shown when an unhandled error is caught',
  }),

  defaultAuthErrorMessageHeader: message({
    id: 'connectEmbed.authError.defaultMessage.header',
    defaultMessage: 'Something went wrong.',

    description:
      'Message shown when an unhandled error is caught during authentication',
  }),

  defaultAuthErrorMessageText: message({
    id: 'connectEmbed.authError.defaultMessage.text',

    defaultMessage: 'There was an error during authentication.',

    description:
      'Message shown when an unhandled error is caught during authentication',
  }),
};

class ConnectElementBaseErrorBoundary extends React.Component<
  ConnectElementBaseErrorBoundaryProps,
  State
> {
  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return {error};
  }

  constructor(props: ConnectElementBaseErrorBoundaryProps) {
    super(props);
    this.state = {error: undefined};
  }

  // eslint-disable-next-line @sail/observability-no-error-boundary
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    const {reportConnectError, onError, hasReportedErrorRef} = this.props;

    // Don't report the error if we've already reported any render errors
    if (hasReportedErrorRef.current) {
      return;
    }
    hasReportedErrorRef.current = true;

    reportConnectError(error, errorInfo);

    // You can execute other logic when an error is captured
    onError && onError(error, errorInfo);
  }

  renderFallbackUI(intl: IntlHook) {
    const {fallbackUI} = this.props;

    if (fallbackUI) {
      return fallbackUI;
    }

    return (
      <ErrorBoundaryFallbackUI
        header={intl.formatMessage(
          defaultErrorMessages.defaultErrorMessageHeader,
        )}
        text={intl.formatMessage(defaultErrorMessages.defaultErrorMessageText)}
      />
    );
  }

  render() {
    const {error} = this.state;
    const {intl, children, hasAuthError} = this.props;

    if (error) {
      return (
        <>
          {this.renderFallbackUI(intl)}
          {isDevEnvironment && (
            <view.p>Dev only: Error is {error.message}</view.p>
          )}
        </>
      );
    }

    // If there was an authentication error, we don't render any UI except a general error
    if (hasAuthError) {
      return (
        <ErrorBoundaryFallbackUI
          header={intl.formatMessage(
            defaultErrorMessages.defaultAuthErrorMessageHeader,
          )}
          text={intl.formatMessage(
            defaultErrorMessages.defaultAuthErrorMessageText,
          )}
        />
      );
    }

    return children;
  }
}

export const ConnectElementErrorBoundary: React.FC<
  ConnectElementErrorBoundaryProps
> = ({uuid, ...props}) => {
  const intl = useIntl();
  const reportConnectError = useReportConnectRenderError();

  // Used to ensure we only report the error once
  const hasReportedErrorRef = React.useRef(false);

  return (
    <ConnectElementBaseErrorBoundary
      uuid={`${uuid}-EDLBII00`}
      {...props}
      intl={intl}
      reportConnectError={reportConnectError}
      hasReportedErrorRef={hasReportedErrorRef}
    />
  );
};
